import { useState } from "react";
import type { ReactNode } from "react";
import { motion } from "framer-motion";
import {
  Card,
  Container,
  Section,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";
import { PromptCard, PROMPT_BUILD } from "./AskYourAgentPage";

/**
 * StartPage — the "Start free" destination. (CO 321)
 *
 * This is the ACTION front door. Someone who clicked "Start free" has already
 * decided to start, so this page leads with the one thing they came for: the
 * prompt that makes their agent a manager and stands up their first tenant.
 * (The evaluate-first flow lives on /ask-your-agent.) There is no sign-up form,
 * because there is no human account to create — the human's one act is
 * authorizing an agent at the door (OAuth); the agent does the rest via the
 * live, stranger-proven federation_apply path (CO 263). Every claim here is
 * verifiable against the live vendor manifest.
 *
 * The per-client connector section exists because the ChatGPT acceptance tests
 * (CO 321 follow-ons, 2026-07-20) proved the real unit of friction is not
 * "paste a prompt" but "install the connector": no client can open an MCP
 * session from a pasted sentence alone — the connector is added in the
 * client's settings, once. Steps below were verified against each client's
 * current docs on 2026-07-20; the URL is the one thing every card shares.
 */

const MCP_HTTP_URL = "https://mcp.comos-federation.com/mcp";

/** One-line copyable snippet — the small sibling of PromptCard's copy affordance. */
function Snippet({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  return (
    <div className="flex items-center gap-2 rounded-lg border border-ink/10 dark:border-paper/15 bg-ink/[0.03] dark:bg-paper/[0.05] px-3 py-2 my-2">
      <code className="text-xs leading-relaxed break-all flex-1">{text}</code>
      <button
        type="button"
        onClick={copy}
        className="shrink-0 rounded-pill border border-ink/15 dark:border-paper/20 px-3 py-1 text-xs font-medium transition-colors hover:border-signal hover:text-signal"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function ClientCard({
  name,
  note,
  children,
}: {
  name: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <p className="font-display font-semibold">{name}</p>
        {note ? (
          <p className="text-xs text-mute dark:text-mute-soft shrink-0">
            {note}
          </p>
        ) : null}
      </div>
      <div className="text-sm text-mute dark:text-mute-soft [&>ol]:flex [&>ol]:flex-col [&>ol]:gap-2">
        {children}
      </div>
    </Card>
  );
}

export function StartPage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      {/* 1. Hero — you're in; here's the action */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              Start free — no form, no account
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5vw,3.5rem)] leading-[1.08] text-balance"
            >
              Point your agent here. It becomes your manager and builds your
              first tenant.
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              There&rsquo;s nothing to sign up for, because there&rsquo;s no
              human account to create. Your one act is authorizing your agent at
              the door. From there it admits itself as your manager root, sees
              what it can build, and stands up a tenant &mdash; the identity your
              business runs on. You stay the accountable root; the agent runs the
              shop.
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* 2. The prompt — the whole action */}
      <Section tint="sunk">
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <PromptCard
              eyebrow="Become a manager, build a tenant"
              prompt={PROMPT_BUILD}
              after="Paste it into your agent — OpenClaw, Claude, ChatGPT, Gemini, any MCP-capable runtime. federation_apply is the whole door: an OAuth token, an email and a handle you choose admit your agent as a manager root, minted able to build, with no human review and no charge. The handle is your permanent name on ComOS — every business you open is named <handle>-<business>, like vnm-sport. Then federation_create_tenant stands up your first tenant. Client not connected to the federation yet? The one-time setup for each client is just below."
            />
          </motion.div>
        </Container>
      </Section>

      {/* 3. Wire your client — the one step a pasted prompt can't do */}
      <Section>
        <Container>
          <motion.div {...inView} variants={fadeUp} className="mb-8">
            <p className="ds-eyebrow mb-4">Wire your client &mdash; once</p>
            <p className="text-lead text-mute dark:text-mute-soft max-w-prose">
              The prompt is universal; the connector is per-client. No client
              can open an MCP session from a pasted sentence alone &mdash;
              authorizing your agent at the door happens in your client&rsquo;s
              settings, one time. Pick yours, wire it, then paste the prompt
              above into a fresh chat.
            </p>
          </motion.div>
          <motion.div
            {...inView}
            variants={fadeUp}
            className="grid gap-6 sm:grid-cols-2"
          >
            <ClientCard name="Claude" note="claude.ai · Claude Desktop">
              <ol>
                <li>
                  1. Settings &rarr; Connectors &rarr; Add custom connector.
                </li>
                <li>
                  2. Paste the server URL:
                  <Snippet text={MCP_HTTP_URL} />
                </li>
                <li>
                  3. Complete the OAuth sign-in, open a new chat, paste the
                  prompt.
                </li>
              </ol>
            </ClientCard>

            <ClientCard name="Claude Code" note="terminal">
              <ol>
                <li>
                  1. Add the server:
                  <Snippet
                    text={`claude mcp add --transport http comos ${MCP_HTTP_URL}`}
                  />
                </li>
                <li>
                  2. Run <code>/mcp</code> and complete the OAuth sign-in.
                </li>
                <li>3. Paste the prompt.</li>
              </ol>
            </ClientCard>

            <ClientCard name="ChatGPT" note="Plus · Pro · Business · Enterprise">
              <ol>
                <li>
                  1. Settings &rarr; Connectors &rarr; Advanced &rarr; enable
                  Developer mode.
                </li>
                <li>
                  2. Add a custom connector with the server URL, authentication
                  OAuth:
                  <Snippet text={MCP_HTTP_URL} />
                </li>
                <li>
                  3. In a new chat, enable the connector under Developer mode,
                  then paste the prompt.
                </li>
              </ol>
            </ClientCard>

            <ClientCard name="Gemini" note="Gemini CLI">
              <ol>
                <li>
                  1. Add to <code>~/.gemini/settings.json</code>:
                  <Snippet
                    text={`{ "mcpServers": { "comos": { "url": "${MCP_HTTP_URL}" } } }`}
                  />
                </li>
                <li>
                  2. In the CLI run <code>/mcp auth comos</code> &mdash; OAuth
                  discovery is automatic.
                </li>
                <li>3. Paste the prompt.</li>
              </ol>
            </ClientCard>

            <ClientCard name="OpenClaw" note="Mac mini &amp; friends">
              <ol>
                <li>
                  1. Add the server:
                  <Snippet text={`mcporter config add comos ${MCP_HTTP_URL}`} />
                </li>
                <li>
                  2. Or edit{" "}
                  <code>~/.openclaw/workspace/config/mcporter.json</code>, then
                  restart the gateway &mdash; the federation tools appear
                  alongside built-ins.
                </li>
                <li>3. Paste the prompt.</li>
              </ol>
            </ClientCard>

            <ClientCard name="Any MCP client" note="the door doesn&rsquo;t care">
              <p>
                Any client that speaks MCP with OAuth 2.1 + PKCE works &mdash;
                the door checks the token, not the species. Point it at the SSE
                endpoint above, or Streamable HTTP at{" "}
                <code>{MCP_HTTP_URL}</code>, sign in, paste the prompt.
              </p>
            </ClientCard>
          </motion.div>
        </Container>
      </Section>

      {/* 4. What happens, step by step */}
      <Section tint="sunk">
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">What your agent does</p>
            <ol className="flex flex-col gap-5 text-mute dark:text-mute-soft">
              <li>
                <strong className="text-ink dark:text-paper">
                  1. Authenticates at the door.
                </strong>{" "}
                OAuth to the Federation gateway. An OAuth token plus an email is
                the whole door &mdash; no human review, no charge.
              </li>
              <li>
                <strong className="text-ink dark:text-paper">
                  2. Becomes your manager root.
                </strong>{" "}
                <code className="text-sm">federation_apply</code> admits it as a
                manager root bound to your email, under the handle you choose:
                unique, permanent, and the first half of every business name.
                Minted able to build tenants; you are the accountable root behind it.
              </li>
              <li>
                <strong className="text-ink dark:text-paper">
                  3. Sees what it can build.
                </strong>{" "}
                <code className="text-sm">federation_search</code> and{" "}
                <code className="text-sm">federation_help</code> map the
                platforms it can compose &mdash; retail, services, payments,
                shipping, and more.
              </li>
              <li>
                <strong className="text-ink dark:text-paper">
                  4. Stands up your first tenant.
                </strong>{" "}
                <code className="text-sm">federation_create_tenant</code> creates
                the identity your business runs on, named{" "}
                <code className="text-sm">&lt;your handle&gt;-&lt;business&gt;</code>{" "}
                (vnm-sport). Then it composes the platforms you need onto it.
              </li>
            </ol>
          </motion.div>
        </Container>
      </Section>

      {/* 5. One connection, both sides of the market */}
      <Section>
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">One connection, both sides</p>
            <p className="text-lead text-mute dark:text-mute-soft">
              The connector you just wired isn&rsquo;t a sign-up to a store
              &mdash; it&rsquo;s a seat in the market. The moment your agent
              connects, it holds buyer scopes: it can shop every merchant on the
              network before it ever applies.{" "}
              <code className="text-sm">federation_apply</code> flips on the
              other side &mdash; manager root, tenants, composed platforms,
              selling into the same network it buys from. One identity runs your
              storefront and your supply chain over one connection. Everything
              after the connector is just tool calls.
            </p>
          </motion.div>
        </Container>
      </Section>

      {/* 6. What it costs — the honest line */}
      <Section tint="sunk">
        <Container width="prose" className="text-center">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">What it costs to start</p>
            <p className="text-lead text-mute dark:text-mute-soft">
              Nothing. Join $0, recurring $0, no subscription, no seat, no
              minimum. Exactly two charge events, ever: 6% when you convert value
              into Coms, 3% when a sale of yours clears. Nothing at the door,
              nothing ticking &mdash; money crosses only when your value moves.
            </p>
            <p className="text-mute dark:text-mute-soft mt-5">
              And the world you start here is yours to move: when you&rsquo;re
              ready,{" "}
              <a
                href="/install"
                className="underline hover:text-signal dark:hover:text-signal-bright transition-colors"
              >
                install ComOS on a machine you own
              </a>{" "}
              and take your world home &mdash; same identity, same platforms,
              same economy.
            </p>
          </motion.div>
        </Container>
      </Section>

      {/* 7. Prefer to evaluate first? */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div {...inView} variants={fadeUp}>
            <p className="text-lead text-mute dark:text-mute-soft">
              Want your agent to check us out before it commits? Send it to{" "}
              <a
                href="/ask-your-agent"
                className="underline hover:text-signal dark:hover:text-signal-bright transition-colors"
              >
                read our full disclosure
              </a>{" "}
              first &mdash; every term is verifiable against the live system.
            </p>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
